// DS EDIT 4 — Document-to-Chat discussion.
// Creates a chat thread about a document/source, seeded with an AI opening
// summary generated through the governed ai-proxy lane. Degrades gracefully:
// if the AI step fails (no credits, timeout, proxy error) the thread is still
// created — just without the opening message.

import { aiService } from "@/services/aiService";
import { getCurrentUserId } from "@/lib/supabase";
import { operatingLoopService } from "@/services/operatingLoopService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { creditsService } from "@/services/creditsService";
import {
  buildOpeningPrompt,
  buildThreadTitle,
  htmlToPlainText,
  truncateForSummary,
  withTimeout,
  DISCUSS_AI_TIMEOUT_MS,
} from "@/lib/discussDocumentPure";

const OPENING_MODEL = "openai/gpt-4o-mini";

export interface StartDiscussionInput {
  itemId: string;
  itemType: "document" | "source";
  title: string | null | undefined;
  contentHtml: string | null | undefined;
  workspaceId?: string | null;
}

export interface StartDiscussionResult {
  threadId: string;
  /** true when the thread opens with an AI-generated summary message */
  aiOpening: boolean;
}

class DiscussDocumentService {
  async startDiscussion(input: StartDiscussionInput): Promise<StartDiscussionResult> {
    const title = buildThreadTitle(input.title);

    let opening: string | null = null;
    try {
      const gate = await creditsService.assertPositiveBalanceForAi();
      if (gate.ok) {
        const plain = truncateForSummary(htmlToPlainText(input.contentHtml));
        if (plain.trim().length > 0) {
          const raw = await withTimeout(
            operatingLoopService.answerWithLoop({
              userId: await getCurrentUserId(),
              question: buildOpeningPrompt(title, plain),
              pickedModel: OPENING_MODEL,
              autoSelectBest: true,
              selectedDocumentIds: input.itemType === "document" ? [input.itemId] : [],
              app: "chat",
              run: async ({ model, briefPrompt }) => ({
                text: await aiService.complete(
                  [briefPrompt, buildOpeningPrompt(title, plain)].filter(Boolean).join("\n\n"),
                  { model, maxTokens: 600, temperature: 0.4, suppressErrors: true, app: "chat" },
                ),
              }),
            }).then((gated) => gated.text),
            DISCUSS_AI_TIMEOUT_MS
          );
          opening = raw?.trim() || null;
        }
      }
    } catch {
      opening = null; // graceful degradation — thread is still created below
    }

    const messages = opening
      ? [
          {
            id: `discuss-${input.itemType}-${Date.now()}`,
            role: "assistant",
            content: opening,
            timestamp: new Date().toISOString(),
            model: OPENING_MODEL,
          },
        ]
      : [];

    const thread = await chatThreadsService.createChatThread(
      { title, messages: messages as never } as never,
      input.workspaceId ?? undefined
    );
    const threadId = String((thread as { id: string | number }).id);
    return { threadId, aiOpening: opening !== null };
  }
}

export const discussDocumentService = new DiscussDocumentService();
